import { ErrorOrError } from "./models/error-or-error.model";
import { IErrorOr } from "./interfaces/error-or.interface";
import { IGenericErrorOr } from "./interfaces/generic-errror-or.interface";

export class ErrorOr<TValue> implements IGenericErrorOr<TValue>, IErrorOr {
    private readonly _value?: TValue;
    private readonly _errors?: ErrorOrError[];
    private static readonly NoFirstError: ErrorOrError = ErrorOrError.unexpected("ErrorOr.NoFirstError", "First error cannot be retrieved from a successful ErrorOr.");
    private static readonly NoErrors: ErrorOrError = ErrorOrError.unexpected("ErrorOr.NoErrors", "ErrorOrError list cannot be retrieved from a successful ErrorOr.");

    public readonly isError: boolean;

    constructor(value?: TValue, errors?: ErrorOrError[]) {
        if (value !== undefined) {
            this._value = value;
            this._errors = undefined;
            this.isError = false;
        } else if (errors !== undefined) {
            this._value = undefined;
            this._errors = errors;
            this.isError = true;
        } else {
            throw new Error("Invalid constructor arguments");
        }
    }

    public get value(): TValue {
        if (this.isError) {
            throw ErrorOr.NoFirstError;
        }
        return this._value!;
    }

    public get errors(): ErrorOrError[] {
        if (this.isError) {
            return this._errors!;
        }
        return [ErrorOr.NoErrors];
    }

    public get errorsOrEmptyList(): ErrorOrError[] {
        return this.isError ? this._errors! : [];
    }

    public static fromErrors<TValue>(errors: ErrorOrError[]): ErrorOr<TValue> {
        return new ErrorOr<TValue>(undefined, errors);
    }

    public static fromValue<TValue>(value: TValue): ErrorOr<TValue> {
        return new ErrorOr<TValue>(value, undefined);
    }

    public else<TNewValue>(onError: (errors: ErrorOrError[]) => TNewValue): ErrorOr<TNewValue> {
        if (this.isError) {
            return new ErrorOr<TNewValue>(onError(this.errors));
        }
        return new ErrorOr<TNewValue>(this.value as unknown as TNewValue);
    }

    public async elseAsync<TNewValue>(onError: (errors: ErrorOrError[]) => Promise<TNewValue>): Promise<ErrorOr<TNewValue>> {
        if (this.isError) {
            return onError(this.errors).then(result => new ErrorOr<TNewValue>(result));
        }
        return await Promise.resolve(new ErrorOr<TNewValue>(this.value as unknown as TNewValue));
    }

    public failIf(onValue: (value: TValue) => boolean, error: ErrorOrError): ErrorOr<TValue> {
        if (this.isError || onValue(this.value!)) {
            return this;
        }
        return new ErrorOr<TValue>(undefined, [error]);
    }

    public match<TNextValue>(onValue: (value: TValue) => TNextValue, onError: (errors: ErrorOrError[]) => TNextValue): TNextValue {
        if (this.isError) {
            return onError(this.errors);
        }
        return onValue(this.value);
    }

    public async matchAsync<TNextValue>(onValue: (value: TValue) => Promise<TNextValue>, onError: (errors: ErrorOrError[]) => Promise<TNextValue>): Promise<TNextValue> {
        if (this.isError) {
            return onError(this.errors);
        }
        return onValue(this.value);
    }

    public matchFirst<TNextValue>(onValue: (value: TValue) => TNextValue, onFirstError: (error: ErrorOrError) => TNextValue): TNextValue {
        if (this.isError) {
            return onFirstError(this.errors[0]);
        }
        return onValue(this.value);
    }

    public async matchFirstAsync<TNextValue>(onValue: (value: TValue) => Promise<TNextValue>, onFirstError: (error: ErrorOrError) => Promise<TNextValue>): Promise<TNextValue> {
        if (this.isError) {
            return onFirstError(this.errors[0]);
        }
        return onValue(this.value);
    }

    public switch(onValue: (value: TValue) => void, onError: (errors: ErrorOrError[]) => void): void {
        if (this.isError) {
            onError(this.errors);
        } else {
            onValue(this.value);
        }
    }

    public firstError(): ErrorOrError {
        if (this.errors.length > 0) {
            return this.errors[0];
        }
        return ErrorOr.NoErrors;
    }

    public static success<TValue>(value: TValue): ErrorOr<TValue> {
        return new ErrorOr<TValue>(value);
    }

    public static failure<TValue>(errors: ErrorOrError[]): ErrorOr<TValue> {
        return ErrorOr.fromErrors(errors);
    }
}
